import { decimalsFor, type CurrencyInfo } from '@/lib/shared-types';
import { api } from '@/lib/api';

/**
 * Currency reference data, fetched once per page load and kept in module scope.
 *
 * Three facts make a module-scoped cache the right shape here:
 *
 *  - `GET /currencies` is small, static platform reference data. Refetching it
 *    per screen would be pure waste.
 *  - Which currency an amount is in is a *property of the branch*, not of the
 *    screen — so every screen must resolve it identically or they disagree.
 *  - Dozens of components read it. A Redux slice would work, but this is
 *    read-mostly data with exactly one writer (Settings), so a tiny store plus
 *    `useSyncExternalStore` buys the same guarantees with less machinery.
 *
 * `useCurrency()` in `@/Hooks/useCurrency` is the public face of this module.
 * Prefer it; reach in here directly only outside React.
 */

/**
 * Last resort, used only when nothing else resolves.
 *
 * The API deliberately has no default — `CurrencyResolverService` throws rather
 * than guess, because denominating a Kuwaiti venue in riyals is a money bug and
 * not a display one. A client still has to put *something* on screen while the
 * reference data is in flight, so this exists for that window and for a caller
 * whose permissions let it read nothing at all. If you see it in a real venue,
 * the tenant has no base currency and that is the bug to fix.
 */
export const FALLBACK_CURRENCY_CODE = 'SAR';

/**
 * Where a resolved code came from. `format()` behaves the same either way; this
 * is what lets Settings say "Inherited from organization (SAR)" rather than
 * rendering a blank.
 */
export type CurrencySource = 'branch' | 'tenant' | 'fallback';

/** `GET /branches/:id/currency` — the server's own answer, chain applied. */
interface ResolvedCurrencyResponse {
  id: string | null;
  code: string;
  symbol: string;
  decimals: number;
  roundingMode: CurrencyInfo['roundingMode'];
  symbolFirst: boolean;
  source: 'BRANCH' | 'TENANT';
}

interface BranchRow {
  /** Null means the branch never had one stamped — it inherits. */
  currencyId: string | null;
  /** Denormalized `Branch.currency`; kept in sync with `currencyId` on write. */
  code: string | null;
}

export interface CurrencyContext {
  /** Active currencies in the platform's own sort order. */
  currencies: CurrencyInfo[];
  byId: Record<string, CurrencyInfo>;
  byCode: Record<string, CurrencyInfo>;
  /** The tenant's base currency, or null when it could not be read. */
  tenant: { currencyId: string | null; code: string | null } | null;
  /** From `GET /branches` — needs `branches.view`, so it can legitimately be empty. */
  branches: Record<string, BranchRow>;
  /** From `GET /branches/:id/currency` — the server's resolution, authoritative. */
  resolved: Record<string, ResolvedCurrencyResponse>;
  /** False while the one-time load is still in flight. */
  ready: boolean;
}

const EMPTY: CurrencyContext = {
  currencies: [],
  byId: {},
  byCode: {},
  tenant: null,
  branches: {},
  resolved: {},
  ready: false,
};

let context: CurrencyContext = EMPTY;
let loadedFor: string | null = null;
let inflight: Promise<CurrencyContext> | null = null;
const branchInflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

function publish(next: CurrencyContext) {
  context = next;
  for (const listener of listeners) listener();
}

/** Bare array or a `{ data, meta }` envelope — tolerate both. */
function rows<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return ((payload as { data?: T[] } | null)?.data ?? []) as T[];
}

/**
 * Branch-list and tenant reads are best-effort on purpose.
 *
 * `GET /branches` needs `branches.view` (D-05 — not `settings.view`) and
 * `GET /tenants/:id` needs a platform key, so a caller can legitimately get a
 * 403 on either. That has to degrade to the next link in the chain, not to a
 * broken screen — money still has to render.
 * `GET /currencies` carries no permission at all, which is why the registry
 * itself is never missing.
 */
async function best<T>(request: Promise<{ data: unknown }>, pick: (data: unknown) => T, fallback: T) {
  try {
    const { data } = await request;
    return pick(data);
  } catch {
    return fallback;
  }
}

async function fetchContext(tenantId: string | null): Promise<CurrencyContext> {
  const [currencies, tenant, branches] = await Promise.all([
    best<CurrencyInfo[]>(
      api.get('/currencies', { params: { activeOnly: true } }),
      (data) => rows<CurrencyInfo>(data),
      [],
    ),
    tenantId
      ? best<CurrencyContext['tenant']>(
          api.get(`/tenants/${tenantId}`),
          (data) => {
            const record = data as { baseCurrencyId?: string | null; currency?: string | null };
            return { currencyId: record?.baseCurrencyId ?? null, code: record?.currency ?? null };
          },
          null,
        )
      : Promise.resolve(null),
    best<Record<string, BranchRow>>(
      api.get('/branches', { params: { limit: 200 } }),
      (data) => {
        const out: Record<string, BranchRow> = {};
        for (const branch of rows<{
          id: string;
          currencyId?: string | null;
          currency?: string | null;
        }>(data)) {
          out[branch.id] = { currencyId: branch.currencyId ?? null, code: branch.currency ?? null };
        }
        return out;
      },
      {},
    ),
  ]);

  const byId: Record<string, CurrencyInfo> = {};
  const byCode: Record<string, CurrencyInfo> = {};
  for (const currency of currencies) {
    if (currency.id) byId[currency.id] = currency;
    byCode[currency.code.toUpperCase()] = currency;
  }

  return { currencies, byId, byCode, tenant, branches, resolved: context.resolved, ready: true };
}

/** The cached context, or a not-ready placeholder before the first load. */
export function getCurrencyContext(): CurrencyContext {
  return context;
}

export function subscribeCurrencyContext(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Loads the shared reference data once. Concurrent callers share one set of
 * requests, and a call after it has landed costs nothing.
 */
export function ensureCurrencyContext(tenantId: string | null): Promise<CurrencyContext> {
  if (context.ready && loadedFor === tenantId) return Promise.resolve(context);
  if (inflight && loadedFor === tenantId) return inflight;
  loadedFor = tenantId;
  inflight = fetchContext(tenantId)
    .then((next) => {
      publish(next);
      return next;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Asks the server to resolve one branch's currency.
 *
 * `GET /branches/:id/currency` carries no permission key — only branch scope —
 * so this works for a waiter or cashier who cannot list branches at all, and it
 * answers with the chain already applied plus which link answered. That makes
 * it the authoritative source; the branch list is only a cheap bulk shortcut.
 */
export function ensureBranchCurrency(branchId: string): Promise<void> {
  if (context.resolved[branchId]) return Promise.resolve();
  const existing = branchInflight.get(branchId);
  if (existing) return existing;

  const request = api
    .get<ResolvedCurrencyResponse>(`/branches/${branchId}/currency`)
    .then(({ data }) => {
      if (!data?.code) return;
      publish({ ...context, resolved: { ...context.resolved, [branchId]: data } });
    })
    .catch(() => {
      // Scope or configuration failure. The list/tenant links still answer.
    })
    .finally(() => {
      branchInflight.delete(branchId);
    });

  branchInflight.set(branchId, request);
  return request;
}

/**
 * Drops the cache and reloads. Call after Settings writes a currency — every
 * screen holding `useCurrency()` repaints with the new answer.
 */
export function refreshCurrencyContext(tenantId: string | null): Promise<CurrencyContext> {
  loadedFor = null;
  inflight = null;
  branchInflight.clear();
  publish({ ...context, resolved: {}, ready: false });
  return ensureCurrencyContext(tenantId);
}

/**
 * A descriptor for a code, guaranteed non-null.
 *
 * When the code is not in the fetched list — it was retired, or the list has
 * not arrived — the ISO minor unit is still the right answer, so it is
 * synthesised rather than returned as null. A caller must never have to decide
 * for itself how many decimals KWD has.
 */
export function currencyInfoFor(code: string, ctx: CurrencyContext = context): CurrencyInfo {
  const upper = (code || FALLBACK_CURRENCY_CODE).toUpperCase();
  const known = ctx.byCode[upper];
  if (known) return known;
  for (const entry of Object.values(ctx.resolved)) {
    if (entry.code.toUpperCase() === upper) {
      return {
        id: entry.id ?? undefined,
        code: upper,
        name: upper,
        symbol: entry.symbol,
        decimals: entry.decimals,
        roundingMode: entry.roundingMode,
        symbolFirst: entry.symbolFirst,
      };
    }
  }
  return {
    code: upper,
    name: upper,
    symbol: upper,
    decimals: decimalsFor(upper),
    roundingMode: 'HALF_UP',
    symbolFirst: true,
  };
}

/**
 * The tenant's base currency code.
 *
 * `GET /tenants/:id` is platform-gated, so for an ordinary tenant admin the
 * answer usually comes from the second source: any branch the server resolved
 * with `source: 'TENANT'` was, by definition, answered from the tenant base.
 */
export function tenantCurrencyCode(ctx: CurrencyContext = context): {
  code: string;
  source: CurrencySource;
} {
  const tenant = ctx.tenant;
  if (tenant?.currencyId && ctx.byId[tenant.currencyId]) {
    return { code: ctx.byId[tenant.currencyId].code.toUpperCase(), source: 'tenant' };
  }
  if (tenant?.code) return { code: tenant.code.toUpperCase(), source: 'tenant' };
  for (const entry of Object.values(ctx.resolved)) {
    if (entry.source === 'TENANT') return { code: entry.code.toUpperCase(), source: 'tenant' };
  }
  return { code: FALLBACK_CURRENCY_CODE, source: 'fallback' };
}

/**
 * Resolves the currency for a branch, falling back to the tenant's base.
 *
 * This mirrors `CurrencyResolverService` on the server, in the same order, so
 * the figure on screen and the figure that gets billed cannot disagree:
 *
 *   1. the server's own answer for this branch, if we asked for it   (BRANCH/TENANT)
 *   2. `Branch.currencyId` from the branch list                      (branch)
 *   3. `Branch.currency`, the denormalized code                      (branch)
 *   4. the tenant base currency                                      (tenant)
 *   5. `FALLBACK_CURRENCY_CODE`                                      (fallback)
 *
 * Step 4 is not a failure state: a branch that inherits is correctly configured,
 * and Settings renders that as "Inherited from organization (SAR)".
 */
export function resolveBranchCurrency(
  branchId: string | null | undefined,
  ctx: CurrencyContext = context,
): { code: string; source: CurrencySource } {
  if (branchId) {
    const resolved = ctx.resolved[branchId];
    if (resolved) {
      return {
        code: resolved.code.toUpperCase(),
        source: resolved.source === 'BRANCH' ? 'branch' : 'tenant',
      };
    }

    const branch = ctx.branches[branchId];
    if (branch?.currencyId) {
      const info = ctx.byId[branch.currencyId];
      if (info) return { code: info.code.toUpperCase(), source: 'branch' };
    }
    if (branch?.currencyId && branch.code) {
      return { code: branch.code.toUpperCase(), source: 'branch' };
    }
  }
  return tenantCurrencyCode(ctx);
}

